import React, { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import jsPDF from 'jspdf';
import { formatMontant } from '../../utils/helpers';
import { MOIS_LABELS } from './comptaUtils';

const COLORS_PIE = ['#CC0000','#FFD700','#1d4ed8','#166534','#7c3aed','#d97706','#0369a1','#374151','#059669','#dc2626'];

export default function OngletBilan({ adherents, comptabilite, selectedYear, cotisations }) {
  // ── Build 12-month summary ──
  const monthData = useMemo(() => {
    let soldeCumul = 0;
    return MOIS_LABELS.map((label, i) => {
      const m   = i + 1;
      const md  = comptabilite[selectedYear]?.[m];
      const ops = md?.operations || [];

      const cotRecues  = ops.filter(o => o.type === 'A').reduce((s,o) => s + o.credit, 0);
      const remiseSEPA = ops.filter(o => o.type === 'C').reduce((s,o) => s + o.credit, 0);
      const autresRec  = ops.filter(o => !['A','B','D','E','F'].includes(o.type) && o.credit > 0).reduce((s,o) => s + o.credit, 0);
      const recettes   = cotRecues + remiseSEPA + autresRec;
      const depenses   = ops.filter(o => o.debit > 0).reduce((s,o) => s + o.debit, 0);
      const impayes    = ops.filter(o => o.type === 'B').reduce((s,o) => s + o.debit, 0);
      const resultat   = recettes - depenses;
      soldeCumul += resultat;

      return {
        mois: label.slice(0, 3), moisFull: label, m,
        cotRecues, remiseSEPA, recettes, depenses, impayes,
        resultat, soldeCumul,
        imported: !!(md?.dateImport || ops.length > 0),
      };
    });
  }, [comptabilite, selectedYear]);

  // ── Category breakdown (all year) ──
  const catBreakdown = useMemo(() => {
    const map = {};
    for (let m = 1; m <= 12; m++) {
      const ops = comptabilite[selectedYear]?.[m]?.operations || [];
      ops.filter(o => o.debit > 0).forEach(o => {
        const cat = o.categorie || 'Divers';
        map[cat] = (map[cat] || 0) + o.debit;
      });
    }
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value);
  }, [comptabilite, selectedYear]);

  // ── Annual KPIs ──
  const kpi = useMemo(() => {
    const totCot     = monthData.reduce((s, m) => s + m.cotRecues + m.remiseSEPA, 0);
    const totDep     = monthData.reduce((s, m) => s + m.depenses, 0);
    const totImp     = monthData.reduce((s, m) => s + m.impayes, 0);
    const totRec     = monthData.reduce((s, m) => s + m.recettes, 0);
    const resultatNet = totRec - totDep;
    const nbImp      = Object.values(comptabilite[selectedYear] || {}).reduce((s, md) =>
      s + (md.operations || []).filter(o => o.type === 'B').length, 0);
    const nbRegul    = Object.values(comptabilite[selectedYear] || {}).reduce((s, md) =>
      s + (md.operations || []).filter(o => o.type === 'B' && o.statut === 'regularise').length, 0);
    const tauxRec    = nbImp > 0 ? Math.round((nbRegul / nbImp) * 100) : 100;
    return { totCot, totDep, totImp, totRec, resultatNet, nbImp, nbRegul, tauxRec };
  }, [monthData, comptabilite, selectedYear]);

  // ── Generate PDF ──
  const generatePDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210, marginL = 15, marginR = 15, contentW = W - marginL - marginR;
    let y = 20;

    const addLine = () => { doc.setDrawColor(200); doc.line(marginL, y, W - marginR, y); y += 4; };
    const text = (t, x, yy, opts = {}) => doc.text(String(t), x, yy, opts);

    // ── Header ──
    doc.setFillColor(204, 0, 0);
    doc.rect(0, 0, W, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    text('SDASSP FO RÉUNION', marginL, 10);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    text(`Rapport financier annuel — Exercice ${selectedYear}`, marginL, 17);
    text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, W - marginR, 17, { align: 'right' });

    doc.setTextColor(0);
    y = 30;

    // ── KPI section ──
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    text('RÉSULTATS DE L\'EXERCICE', marginL, y); y += 8;
    addLine();

    const kpiRows = [
      ['Total cotisations encaissées', formatMontant(kpi.totCot)],
      ['Total dépenses', formatMontant(kpi.totDep)],
      ['Résultat net de l\'exercice', formatMontant(kpi.resultatNet)],
      ['Nombre d\'impayés', String(kpi.nbImp)],
      ['Taux de recouvrement', `${kpi.tauxRec} %`],
    ];

    doc.setFontSize(10);
    kpiRows.forEach(([label, val]) => {
      doc.setFont('helvetica', 'normal'); text(label, marginL, y);
      doc.setFont('helvetica', 'bold');   text(val, W - marginR, y, { align: 'right' });
      y += 7;
    });
    y += 4;

    // ── Monthly table ──
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    text('TABLEAU MENSUEL DÉTAILLÉ', marginL, y); y += 8;
    addLine();

    // Table header
    doc.setFillColor(230, 230, 230);
    doc.rect(marginL, y - 5, contentW, 7, 'F');
    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    const cols = [marginL, 40, 80, 120, 150, 180];
    const headers = ['Mois', 'Cotisations', 'Dépenses', 'Impayés', 'Résultat', 'Solde cumulé'];
    headers.forEach((h, i) => text(h, cols[i], y, { align: i === 0 ? 'left' : 'right' }));
    y += 3; addLine();

    doc.setFont('helvetica', 'normal');
    monthData.forEach(md => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFontSize(8);
      text(md.moisFull, cols[0], y);
      text(formatMontant(md.cotRecues + md.remiseSEPA), cols[1], y, { align: 'right' });
      text(formatMontant(md.depenses), cols[2], y, { align: 'right' });
      text(formatMontant(md.impayes), cols[3], y, { align: 'right' });
      doc.setTextColor(md.resultat >= 0 ? 22 : 153, md.resultat >= 0 ? 101 : 27, md.resultat >= 0 ? 52 : 27);
      text(formatMontant(md.resultat), cols[4], y, { align: 'right' });
      doc.setTextColor(0);
      text(formatMontant(md.soldeCumul), cols[5], y, { align: 'right' });
      y += 6;
      doc.setDrawColor(240); doc.line(marginL, y - 1, W - marginR, y - 1);
    });

    // Totals row
    y += 2;
    doc.setFillColor(255, 215, 0);
    doc.rect(marginL, y - 5, contentW, 7, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    text('TOTAL', cols[0], y);
    text(formatMontant(kpi.totRec), cols[1], y, { align: 'right' });
    text(formatMontant(kpi.totDep), cols[2], y, { align: 'right' });
    text(formatMontant(kpi.totImp), cols[3], y, { align: 'right' });
    doc.setTextColor(kpi.resultatNet >= 0 ? 22 : 153, kpi.resultatNet >= 0 ? 101 : 27, kpi.resultatNet >= 0 ? 52 : 27);
    text(formatMontant(kpi.resultatNet), cols[4], y, { align: 'right' });
    doc.setTextColor(0);
    y += 10;

    // ── Dépenses par catégorie ──
    if (catBreakdown.length > 0) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(13); doc.setFont('helvetica', 'bold');
      text('RÉPARTITION DES DÉPENSES PAR CATÉGORIE', marginL, y); y += 8; addLine();
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      catBreakdown.forEach(cat => {
        if (y > 270) { doc.addPage(); y = 20; }
        text(cat.name, marginL, y);
        text(formatMontant(cat.value), W - marginR, y, { align: 'right' });
        y += 6;
      });
      y += 4;
    }

    // ── Footer AG ──
    if (y > 250) { doc.addPage(); y = 20; }
    y = Math.max(y, 240);
    doc.setDrawColor(204, 0, 0); doc.setLineWidth(0.5);
    doc.line(marginL, y, W - marginR, y); y += 8;
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    text('Approuvé en Assemblée Générale le : ____________________________', marginL, y); y += 10;
    doc.setFont('helvetica', 'normal');
    text('Signature du Trésorier : ____________________________', marginL, y); y += 8;
    text('Signature du Secrétaire : ____________________________', marginL, y);

    doc.save(`rapport_financier_SDASSP_FO_${selectedYear}.pdf`);
  };

  return (
    <div>
      {/* Header buttons */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button className="btn btn-primary" onClick={generatePDF}>
          📄 Générer le rapport annuel PDF
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid-3 mb-4" style={{ gap:14 }}>
        {[
          { label:`Cotisations encaissées ${selectedYear}`, val: formatMontant(kpi.totCot), color:'#166534' },
          { label:'Total dépenses',                         val: formatMontant(kpi.totDep), color:'#991b1b' },
          { label:'Résultat net exercice',                  val: formatMontant(kpi.resultatNet), color: kpi.resultatNet >= 0 ? '#166534' : '#991b1b' },
          { label:'Impayés détectés',                       val: kpi.nbImp,                  color:'#d97706' },
          { label:'Taux de recouvrement',                   val: `${kpi.tauxRec} %`,          color: kpi.tauxRec >= 90 ? '#166534' : '#d97706' },
        ].map(k => (
          <div key={k.label} className="stat-card" style={{ borderTopColor: k.color }}>
            <div className="stat-label">{k.label}</div>
            <div className="stat-value" style={{ color: k.color }}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* Tableau mensuel */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="card-title">📅 Synthèse mensuelle {selectedYear}</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Mois</th>
                <th style={{ textAlign:'right' }}>Cotisations</th>
                <th style={{ textAlign:'right' }}>Dépenses</th>
                <th style={{ textAlign:'right' }}>Impayés</th>
                <th style={{ textAlign:'right' }}>Résultat</th>
                <th style={{ textAlign:'right' }}>Solde cumulé</th>
                <th style={{ textAlign:'center', width:70 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {monthData.map(md => (
                <tr key={md.m}>
                  <td style={{ fontWeight:600 }}>{md.moisFull}</td>
                  <td style={{ textAlign:'right', color:'#166534', fontWeight:700 }}>{md.cotRecues > 0 ? formatMontant(md.cotRecues + md.remiseSEPA) : '—'}</td>
                  <td style={{ textAlign:'right', color:'#991b1b' }}>{md.depenses > 0 ? formatMontant(md.depenses) : '—'}</td>
                  <td style={{ textAlign:'right', color:'#d97706' }}>{md.impayes > 0 ? formatMontant(md.impayes) : '—'}</td>
                  <td style={{ textAlign:'right', fontWeight:700, color: md.resultat >= 0 ? '#166534' : '#991b1b' }}>
                    {md.recettes + md.depenses > 0 ? formatMontant(md.resultat) : '—'}
                  </td>
                  <td style={{ textAlign:'right', fontWeight:700, color: md.soldeCumul >= 0 ? '#166534' : '#991b1b' }}>
                    {formatMontant(md.soldeCumul)}
                  </td>
                  <td style={{ textAlign:'center' }}>
                    {md.imported
                      ? <span style={{ fontSize:11, color:'#166534' }}>✅ Importé</span>
                      : <span style={{ fontSize:11, color:'#d97706' }}>⚠️ Manquant</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop:'2px solid #e5e7eb', background:'#fef9c3' }}>
                <td style={{ fontWeight:800 }}>TOTAL</td>
                <td style={{ textAlign:'right', fontWeight:800, color:'#166534' }}>{formatMontant(kpi.totRec)}</td>
                <td style={{ textAlign:'right', fontWeight:800, color:'#991b1b' }}>{formatMontant(kpi.totDep)}</td>
                <td style={{ textAlign:'right', fontWeight:800, color:'#d97706' }}>{formatMontant(kpi.totImp)}</td>
                <td style={{ textAlign:'right', fontWeight:800, color: kpi.resultatNet >= 0 ? '#166534' : '#991b1b' }}>{formatMontant(kpi.resultatNet)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="grid-2 mb-4" style={{ gap:16 }}>
        {/* Line chart solde cumulé */}
        <div className="card">
          <div className="card-header"><div className="card-title">📈 Évolution du solde</div></div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthData} margin={{ top:10, right:20, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:10 }} tickFormatter={v => `${Math.round(v/1000)}k`} />
              <Tooltip formatter={v => formatMontant(v)} />
              <Line type="monotone" dataKey="soldeCumul" stroke="#CC0000" strokeWidth={2} dot={{ r:3 }} name="Solde cumulé" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart recettes vs dépenses */}
        <div className="card">
          <div className="card-header"><div className="card-title">📊 Recettes vs Dépenses</div></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthData} margin={{ top:10, right:20, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:10 }} tickFormatter={v => `${Math.round(v/100)/10}k`} />
              <Tooltip formatter={v => formatMontant(v)} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Bar dataKey="recettes" name="Recettes" fill="#166534" radius={[3,3,0,0]} />
              <Bar dataKey="depenses" name="Dépenses" fill="#CC0000"  radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid-2 mb-4" style={{ gap:16 }}>
        {/* Donut dépenses par catégorie */}
        <div className="card">
          <div className="card-header"><div className="card-title">🍩 Dépenses par catégorie</div></div>
          {catBreakdown.length === 0 ? (
            <div className="empty-state" style={{ padding:'40px 0' }}>
              <div className="empty-state-text">Aucune dépense enregistrée</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={catBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({ name, percent }) => `${name.slice(0,12)} ${(percent*100).toFixed(0)}%`}
                  labelLine={false}>
                  {catBreakdown.map((_, i) => <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                </Pie>
                <Tooltip formatter={v => formatMontant(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bar cotisations vs impayés */}
        <div className="card">
          <div className="card-header"><div className="card-title">📉 Cotisations vs Impayés</div></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthData} margin={{ top:10, right:20, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:10 }} tickFormatter={v => `${Math.round(v/100)/10}k`} />
              <Tooltip formatter={v => formatMontant(v)} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Bar dataKey="cotRecues" name="Cotisations"  fill="#166534" radius={[3,3,0,0]} />
              <Bar dataKey="impayes"   name="Impayés rejetés" fill="#d97706" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
